import React, { useState, useEffect } from 'react';
import SEO from '../../components/SEO';
import { TAROT_CARDS } from '../../data/fortuneData';
import TarotCard from '../../components/fortune/TarotCard';
import FortuneContent from '../../components/fortune/FortuneContent';
import AdBanner from '../../components/AdBanner';
import { getTarotCard } from '../../services/api';
import './FortuneCategoryPage.css';

function TarotPage() {
  const [fortune, setFortune] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fortuneCache, setFortuneCache] = useState(new Map());

  useEffect(() => {
    document.title = '타로 카드 - 로또운세';

    // Prefetch all tarot cards
    const fetchAllCards = async () => {
      const cache = new Map();

      try {
        // Fetch all 22 tarot cards in parallel
        const promises = TAROT_CARDS.map(card =>
          getTarotCard(card.id)
            .then(response => {
              if (response.success) {
                cache.set(card.id, {
                  title: `${card.emoji} ${card.kr} (${card.name})`,
                  date: response.data.date,
                  overall: response.data.meaning,
                  advice: response.data.advice,
                  warning: response.data.warning,
                  insight: response.data.insight,
                  luckyNumbers: response.data.luckyNumbers,
                  luckyColor: response.data.luckyColor,
                  luckyDirection: response.data.luckyDirection
                });
              }
            })
            .catch(err => console.error(`Error fetching tarot ${card.id}:`, err))
        );

        await Promise.all(promises);
        setFortuneCache(cache);
      } catch (error) {
        console.error('Error prefetching tarot cards:', error);
      }
    };

    fetchAllCards();
  }, []);

  const handleSelectCard = (card) => {
    // Get from cache instantly
    const cachedFortune = fortuneCache.get(card.id);

    if (cachedFortune) {
      setFortune(cachedFortune);

      // Scroll to fortune content
      setTimeout(() => {
        const element = document.querySelector('.fortune-content');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      // Fallback to API if cache miss
      fetchCardFromAPI(card);
    }
  };

  const fetchCardFromAPI = async (card) => {
    setLoading(true);

    try {
      const response = await getTarotCard(card.id);
      if (response.success) {
        const fortuneData = {
          title: `${card.emoji} ${card.kr} (${card.name})`,
          date: response.data.date,
          overall: response.data.meaning,
          advice: response.data.advice,
          warning: response.data.warning,
          insight: response.data.insight,
          luckyNumbers: response.data.luckyNumbers,
          luckyColor: response.data.luckyColor,
          luckyDirection: response.data.luckyDirection
        };

        setFortune(fortuneData);

        // Scroll to fortune content
        setTimeout(() => {
          const element = document.querySelector('.fortune-content');
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching tarot card:', error);
      alert('타로 카드를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleRandomCard = () => {
    const randomCard = TAROT_CARDS[Math.floor(Math.random() * TAROT_CARDS.length)];
    handleSelectCard(randomCard);
  };

  return (
    <div className="fortune-category-page">
      <SEO
        title="AI 타로 카드 운세 - 인공지능 타로 로또 운세"
        description="AI 타로 카드로 오늘의 로또 운세를 확인하세요. 인공지능이 해석하는 메이저 아르카나 22장의 타로 메시지와 AI 행운의 숫자를 무료로 제공합니다."
        keywords="AI 타로, AI 타로 운세, 인공지능 타로, GPT 타로, 타로 로또, 타로 운세, AI 운세, 인공지능 운세, 로또운세, 로또 운세, 오늘의 타로, 타로카드, AI 행운의 숫자"
        url="https://lottounse.kr/fortune/tarot"
      />
      <div className="category-header tarot-header">
        <h1>🃏 타로 카드</h1>
        <p>마음속으로 질문을 떠올리며 카드를 선택하세요</p>
        <button className="random-btn" onClick={handleRandomCard}>
          🎲 랜덤으로 카드 뽑기
        </button>
      </div>

      <AdBanner />

      <div className="category-content">
        <div className="cards-grid tarot-grid">
          {TAROT_CARDS.map((card) => (
            <TarotCard
              key={card.id}
              card={card}
              onClick={handleSelectCard}
            />
          ))}
        </div>

        {loading && (
          <div className="loading-section">
            <p>카드의 메시지를 읽는 중...</p>
          </div>
        )}

        {fortune && !loading && (
          <>
            <AdBanner />
            <FortuneContent fortune={fortune} type="tarot" />
          </>
        )}
      </div>

      <AdBanner />
    </div>
  );
}

export default TarotPage;
